import React, { useEffect, useState } from 'react';
import YearChangePanel from '../components/YearChangePanel';
import Notice from '../components/Notice';
import { confirmDialog } from '../components/ConfirmDialog';

// 履修者名簿の取り込み履歴【2026-09-14】【2026-09-17 年度の直し方を作り直し】
//   年度・学期は「取り込み1回ぶんの属性」なので、直すのも取り消すのもここから。
//   取り込み時に年度を選び間違える事故が一番ありがちなので、
//   取り込み（manabaのファイル）ごとにまとめて直せる入口を用意しておく。
//
//   直す前に必ず「どうなるか」を見せる（YearChangePanel）。
//   正しい年度に同じ名前の授業があれば警告し、合流か保留かを選ばせる。
//   年度が分からない授業は「保留中」に逃がしておき、後でここから年度を決める。
//
//   embedded … 学生管理の「名簿の取り込み」タブの中に置くとき（見出しを小さくする）
const HOW_LABEL = { created: '新規', matched: '既存に追加', mapped: '指定', forced: '指定' };

export default function RosterImports({ embedded = false }) {
  const [imports, setImports] = useState([]);
  const [held, setHeld]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState('');
  const [msg, setMsg]         = useState('');
  const [open, setOpen]       = useState(null);   // { mode, id, title, year, termId }
  const [expanded, setExpanded] = useState({});   // 取り込みID → 授業を開いているか

  const auth = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const [listRes, heldRes] = await Promise.all([
        fetch('/api/roster-imports',              { headers: auth() }),
        fetch('/api/roster-imports/held-classes', { headers: auth() }),
      ]);
      const list = await listRes.json();
      const hd   = await heldRes.json();
      if (!list.success) throw new Error(list.message || '取得に失敗しました');
      setImports(list.imports || []);
      setHeld(hd.success ? (hd.classes || []) : []);
    } catch (e) { setErr(e.message); }
    finally     { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const done = (message) => {
    setOpen(null);
    setMsg(message || '変更しました');
    load();
  };

  // 取り消しは履修登録だけ。授業と出席記録は残る（サーバー側でそうしている）
  const revert = async (row) => {
    if (!(await confirmDialog(
      `「${row.fileName || '(ファイル名なし)'}」で入った履修登録 ${row.enrolledCount} 件を取り消します。\n` +
      '授業と出席記録は消えません。よろしいですか？'
    ))) return;
    setErr(''); setMsg('');
    try {
      const res = await fetch('/api/roster-imports/' + row.id, {
        method: 'DELETE', headers: auth(),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || '取り消しに失敗しました');
      setMsg(data.message);
      await load();
    } catch (e) { setErr(e.message); }
  };

  const fmt = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const Heading = embedded ? 'h3' : 'h2';

  return (
    <div className={embedded ? undefined : 'page-container'}>
      <Heading style={embedded ? { margin: '0 0 0.3rem' } : undefined}>名簿の取り込み履歴</Heading>
      <p style={{ color: '#666', fontSize: '0.9rem', marginTop: 0 }}>
        manabaのファイルを取り込んだ1回ごとに、年度・学期をまとめて直せます。
        直す前に「どの授業がどうなるか」を確認できます。正しい年度に同じ名前の授業があるときは警告が出るので、
        合流させるか、その授業も年度が怪しければ「保留」に移すかを選んでください。
      </p>

      <Notice kind="error" compact>{err}</Notice>
      <Notice kind="success" toast>{msg}</Notice>

      <button className="btn-secondary" onClick={load} disabled={loading}>
        {loading ? '読み込み中…' : '再読み込み'}
      </button>

      {/* 保留中の授業 */}
      {held.length > 0 && (
        <div style={{ margin: '1rem 0', border: '1px solid #fcd34d', background: '#fffbeb',
                      borderRadius: 8, padding: '0.7rem 0.9rem' }}>
          <strong>年度保留中の授業（{held.length}）</strong>
          <div style={{ fontSize: '0.82rem', color: '#92400e', margin: '0.2rem 0 0.5rem' }}>
            年度の一覧には出ていません。正しい年度が分かったら「年度を決める」で戻してください。
            打刻や今日の授業には影響していません。
          </div>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr><th>授業</th><th>元の年度</th><th>履修者</th><th>実施</th><th>メモ</th><th></th></tr>
            </thead>
            <tbody>
              {held.map(c => (
                <tr key={c.id}>
                  <td>{c.name}{c.teacherName && <div style={{ fontSize: '0.75rem', color: '#888' }}>{c.teacherName}</div>}</td>
                  <td>{c.academicYear ? c.academicYear + '年度' : '—'}{c.termName || ''}</td>
                  <td>{c.enrolled}</td>
                  <td>{c.sessions}回</td>
                  <td style={{ fontSize: '0.8rem', color: '#555' }}>{c.note || ''}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button onClick={() => { setMsg(''); setOpen({
                      mode: 'class', id: c.id, title: `「${c.name}」の年度を決める`,
                      year: c.academicYear, termId: c.termId,
                    }); }}>年度を決める</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <table className="data-table" style={{ marginTop: 12, width: '100%' }}>
        <thead>
          <tr>
            <th>取り込み日時</th>
            <th>年度・学期</th>
            <th>ファイル</th>
            <th>授業</th>
            <th>履修登録</th>
            <th>担当教員</th>
            <th>実行者</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {imports.length === 0 && !loading && (
            <tr><td colSpan={8} style={{ textAlign: 'center', color: '#888' }}>
              取り込み履歴はまだありません
            </td></tr>
          )}
          {imports.map((row) => (
            <React.Fragment key={row.id}>
              <tr style={row.revertedAt ? { opacity: 0.5 } : undefined}>
                <td>{fmt(row.importedAt)}</td>
                <td><strong>{row.academicYear}年度{row.termName || ''}</strong></td>
                <td>{row.fileName || '—'}</td>
                <td>
                  <button className="btn-secondary" style={{ padding: '0.1rem 0.5rem' }}
                          onClick={() => setExpanded({ ...expanded, [row.id]: !expanded[row.id] })}
                          title="この取り込みで入った授業を表示">
                    {row.classes.length || row.classesCount}件 {expanded[row.id] ? '▲' : '▼'}
                  </button>
                </td>
                <td>{row.enrolledCount}</td>
                <td>{row.teacherName || '—'}</td>
                <td>{row.importedBy || '—'}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button onClick={() => { setMsg(''); setOpen({
                    mode: 'batch', id: row.id,
                    title: `「${row.fileName || '取り込み #' + row.id}」の年度を直す`,
                    year: row.academicYear, termId: row.termId,
                  }); }}>年度を直す</button>
                  {!row.revertedAt && (
                    <button onClick={() => revert(row)} style={{ marginLeft: 4 }}>取り消す</button>
                  )}
                  {row.revertedAt && <span style={{ color: '#888', marginLeft: 4 }}>取消済</span>}
                </td>
              </tr>
              {expanded[row.id] && (
                <tr>
                  <td colSpan={8} style={{ background: '#f9fafb' }}>
                    {row.classes.length === 0 ? (
                      <span style={{ color: '#888', fontSize: '0.85rem' }}>授業の記録がありません</span>
                    ) : (
                      <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.85rem' }}>
                        {row.classes.map(c => (
                          <li key={c.classId} style={{ margin: '0.15rem 0' }}>
                            <strong>{c.className}</strong>
                            {c.courseName && c.courseName !== c.className && (
                              <span style={{ color: '#888' }}>（ファイル上: {c.courseName}）</span>
                            )}
                            {' '}— {c.onHold ? '保留中' : `${c.academicYear ?? '年度なし'}年度${c.termName || ''}`}
                            {' '}・履修 {c.enrolled}人
                            <span style={{ marginLeft: 6, fontSize: '0.72rem', color: '#6b7280',
                                           border: '1px solid #d1d5db', borderRadius: 4, padding: '0 4px' }}>
                              {HOW_LABEL[c.how] || c.how}
                            </span>
                            {c.archived && <span style={{ marginLeft: 4, fontSize: '0.72rem', color: '#9ca3af' }}>畳んだ授業</span>}
                            {c.academicYear !== row.academicYear && !c.onHold && ['created', 'matched'].includes(c.how) && (
                              <span style={{ marginLeft: 6, fontSize: '0.75rem', color: '#b45309', fontWeight: 700 }}>
                                取り込みの年度と違います
                              </span>
                            )}
                            <button style={{ marginLeft: 8, padding: '0 0.4rem', fontSize: '0.75rem' }}
                                    onClick={() => { setMsg(''); setOpen({
                                      mode: 'class', id: c.classId, title: `授業「${c.className}」の年度を直す`,
                                      year: c.academicYear, termId: c.termId,
                                    }); }}>
                              この授業だけ直す／保留
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>

      {open && (
        <YearChangePanel
          modal
          key={open.mode + open.id}
          mode={open.mode}
          targetId={open.id}
          title={open.title}
          initialYear={open.year}
          initialTermId={open.termId}
          onDone={done}
          onCancel={() => setOpen(null)}
        />
      )}
    </div>
  );
}
